import classNames from "classnames"

import CategoryBadge from "./CategoryBadge"
import { WIDE_ARTICLE_SRCSET_WIDTHS } from "./imageConfig"

import Link from "@components/elements/Link"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import Typography from "@components/elements/V2/Typography"
import Video from "@components/experiments/Video"
import LayoutItem from "@components/V2/Layouts/LayoutItem"
import { Color } from "@constants/V2/color"
import {
  getTextColorClass,
  getTransparentBorderColorClass,
} from "@utils/V2/color"
import React from "react"

const VIDEO_CTA_TEXT = "View article"

type VideoArticleCardProps = {
  title: string
  coverImageUrl: string
  coverImageAlt: string
  category?: string
  summary?: string
  readTimeText?: string
  articleLink?: YocoCom.YocoLink
  videoUrl?: string
  textColor: Color
}

const VideoArticleCard = ({
  title,
  coverImageUrl,
  coverImageAlt,
  category,
  summary,
  readTimeText,
  articleLink,
  videoUrl,
  textColor,
}: VideoArticleCardProps) => {
  return (
    <LayoutItem
      animateOnScroll
      className={classNames(
        "group col-span-full flex flex-col border-b md:ml-10 md:flex-row md:justify-between md:border-b-0",
        getTransparentBorderColorClass(textColor)
      )}
    >
      <div className="relative aspect-video overflow-hidden rounded-8 md:w-[64.76%] md:rounded-16">
        <div className="absolute inset-0">
          {videoUrl ? (
            <Video
              video={videoUrl}
              coverImage={coverImageUrl}
              ImageAlt={coverImageAlt}
              videoTitle={title}
              aspectRatio="fullWidth"
              playbackMode="manual"
              loadType="lazy"
              sizes="(min-width: 1200px) 55vw, (min-width: 900px) 58vw, 92vw"
              srcSetWidths={WIDE_ARTICLE_SRCSET_WIDTHS}
              className="h-full w-full"
            />
          ) : (
            <GatsbyStoryblokImage
              image={coverImageUrl}
              alt={coverImageAlt}
              aspectRatio="fullWidth"
              imageObjectFit="cover"
              sizes="(min-width: 1200px) 55vw, (min-width: 900px) 58vw, 92vw"
              srcSetWidths={WIDE_ARTICLE_SRCSET_WIDTHS}
              preserveAnimation
              className="h-full w-full"
            />
          )}
        </div>
      </div>

      <div className="flex flex-col justify-center gap-16 py-10 md:w-[30.17%] md:gap-20 md:py-0">
        {category && (
          <CategoryBadge category={category} className="hidden md:block" />
        )}

        <Typography
          className="!text-body-md md:!text-lead-lg md:transition-colors md:duration-200 md:ease-out md:group-focus-within:text-blue md:group-hover:text-blue"
          element="h2"
          size="lead-lg"
          font="grotesk"
          weight="medium"
          color={textColor}
          text={title}
        />

        {summary && (
          <Typography
            className="hidden opacity-80 md:block"
            size="body-md"
            font="grotesk"
            weight="book"
            color={textColor}
            text={summary}
          />
        )}

        {(category || readTimeText) && (
          <div className="flex items-center gap-8 md:hidden">
            {category && <CategoryBadge category={category} />}

            {readTimeText && (
              <Typography
                className="opacity-60"
                size="body-sm"
                font="grotesk"
                weight="book"
                color={textColor}
                text={readTimeText}
              />
            )}
          </div>
        )}

        {articleLink && (
          <Link
            link={articleLink}
            className={classNames(
              "group -mt-12 flex w-fit cursor-pointer items-center gap-10 py-12",
              getTextColorClass(textColor)
            )}
          >
            <Typography
              size="body-sm"
              font="grotesk"
              weight="medium"
              color={textColor}
              text={VIDEO_CTA_TEXT}
            />
            <span className="flex h-20 w-20 flex-none items-center justify-center">
              <ArrowIcon />
            </span>
          </Link>
        )}
      </div>
    </LayoutItem>
  )
}

export default VideoArticleCard
